import { KycQuestionnaireQuestionNames } from '~/enums';
import {
  IKYCQuestionnaireAnswer,
  IKYCQuestionnaireQuestionWithAnswers,
} from '~/interfaces/shared';

/**
 * The KYC questionnaire has one dependent pair of questions:
 * "Do you handle cash?" (YES/NO) and the follow-up "How much cash do you handle?".
 * The follow-up is only shown when the cash handling answer is YES.
 * Both questions exist in an old and a new wording, depending on the market's questionnaire.
 */
const CASH_HANDLING_QUESTION_NAMES: readonly string[] = [
  KycQuestionnaireQuestionNames.handleCashQuestion,
  KycQuestionnaireQuestionNames.handleCashQuestionNew,
];

const CASH_AMOUNT_QUESTION_NAMES: readonly string[] = [
  KycQuestionnaireQuestionNames.handleCashToWhatExtentQuestion,
  KycQuestionnaireQuestionNames.handleCashPercentageTurnoverQuestion,
];

const CASH_HANDLING_YES = 'YES';
const CASH_HANDLING_NO = 'NO';

const isCashHandlingQuestion = (
  question: IKYCQuestionnaireQuestionWithAnswers
): boolean => CASH_HANDLING_QUESTION_NAMES.includes(question.name);

export const isCashAmountQuestion = (
  question: IKYCQuestionnaireQuestionWithAnswers
): boolean => CASH_AMOUNT_QUESTION_NAMES.includes(question.name);

const findAnswer = (
  answers: IKYCQuestionnaireAnswer[],
  questionId: number | undefined
): IKYCQuestionnaireAnswer | undefined =>
  answers.find((answer) => answer.questionId === questionId);

const hasAnswerValue = (
  answer: IKYCQuestionnaireAnswer | undefined,
  value: string
): boolean => !!answer?.answers.some((option) => option.answer === value);

/** Replaces the answer for the same question, or appends it if the question is unanswered. */
const upsertAnswer = (
  answers: IKYCQuestionnaireAnswer[],
  newAnswer: IKYCQuestionnaireAnswer
): IKYCQuestionnaireAnswer[] =>
  findAnswer(answers, newAnswer.questionId)
    ? answers.map((answer) =>
        answer.questionId === newAnswer.questionId ? newAnswer : answer
      )
    : [...answers, newAnswer];

/**
 * Returns the questions the user should see: the cash amount question is hidden
 * unless the user answered YES to the cash handling question.
 */
export const getVisibleKYCQuestions = (
  questions: IKYCQuestionnaireQuestionWithAnswers[],
  answers: IKYCQuestionnaireAnswer[]
): IKYCQuestionnaireQuestionWithAnswers[] => {
  const cashHandlingQuestionId = questions.find(isCashHandlingQuestion)?.id;
  const handlesCash = hasAnswerValue(
    findAnswer(answers, cashHandlingQuestionId),
    CASH_HANDLING_YES
  );

  if (handlesCash) {
    return questions;
  }
  return questions.filter((question) => !isCashAmountQuestion(question));
};

/**
 * Counts questions with an actual answer. Cleared answers (empty `answers` array)
 * are not counted, since they only exist to reset the saved value in the backend.
 */
export const countAnsweredKYCQuestions = (
  answers: IKYCQuestionnaireAnswer[]
): number => answers.filter((answer) => answer.answers.length > 0).length;

interface IApplyKYCAnswerParams {
  answers: IKYCQuestionnaireAnswer[];
  questions: IKYCQuestionnaireQuestionWithAnswers[];
  newAnswer: IKYCQuestionnaireAnswer;
}

/**
 * Saves a KYC answer. When the user answers NO to the cash handling question, the
 * cash amount answer is cleared by setting it to an empty `answers` array.
 *
 * Why it's cleared instead of removed: the backend keeps the previously saved answer
 * for any question missing from the payload, and has no concept of dependent questions.
 * Sending an empty `answers` array is the only way to overwrite an old cash amount.
 */
export const applyKYCAnswer = ({
  answers,
  questions,
  newAnswer,
}: IApplyKYCAnswerParams): IKYCQuestionnaireAnswer[] => {
  const updatedAnswers = upsertAnswer(answers, newAnswer);

  const isCashHandlingAnswer =
    newAnswer.questionId === questions.find(isCashHandlingQuestion)?.id;
  const cashAmountQuestionId = questions.find(isCashAmountQuestion)?.id;

  if (
    !isCashHandlingAnswer ||
    !hasAnswerValue(newAnswer, CASH_HANDLING_NO) ||
    cashAmountQuestionId === undefined
  ) {
    return updatedAnswers;
  }

  return upsertAnswer(updatedAnswers, {
    questionId: cashAmountQuestionId,
    answers: [],
  });
};
